import {
  questAutoSummary,
  questMetaDescriptionText,
  type QuestSeoFacts,
} from "../../shared/quest-seo.ts";
import { spotPathDistanceM, sumEstMinutes } from "../../shared/mission-quest.ts";
import { locale, type Locale } from "../i18n/index.ts";
import type { QuestDetail } from "./types.ts";

function toSeoLocale(lang: Locale): "en" | "ko" {
  return lang === "ko" ? "ko" : "en";
}

function spotTitle(spot: QuestDetail["checkpoints"][number] | undefined): string | null {
  const text = spot?.title?.trim();
  return text || null;
}

export function questSeoFactsFromDetail(q: QuestDetail): QuestSeoFacts | null {
  if (q.quest_type === "spot_trace" && q.checkpoints.length >= 1) {
    const spots = q.checkpoints.map((s) => ({ lat: s.lat, lng: s.lng }));
    const start = q.checkpoints.find((s) => s.kind === "start");
    const finish = q.checkpoints.find((s) => s.kind === "finish");
    return {
      id: q.id,
      title: q.title,
      description: q.description ?? "",
      author: q.author,
      city: q.city,
      country: q.country,
      routePlace: q.route_place ?? null,
      routeLengthM: Math.round(spotPathDistanceM(spots)),
      startFixed: Boolean(q.start_fixed),
      timeLimitS: q.time_limit_s ?? null,
      startTitle: start?.title?.trim() || null,
      finishTitle: finish?.title?.trim() || null,
      status: q.status,
      createdAt: q.created_at,
      publishedAt: q.published_at ?? null,
      questType: "spot_trace",
      spotCount: q.checkpoints.length,
      estMinutesTotal: sumEstMinutes(q.checkpoints),
    };
  }
  const route = q.route;
  if (route && route.coordinates.length >= 2) {
    return {
      id: q.id,
      title: q.title,
      description: q.description ?? "",
      author: q.author,
      city: q.city,
      country: q.country,
      routePlace: q.route_place ?? null,
      routeLengthM: route.length_m,
      startFixed: Boolean(q.start_fixed),
      timeLimitS: q.time_limit_s ?? null,
      startTitle: q.start_fixed ? spotTitle(q.checkpoints.find((s) => s.kind === "start")) : null,
      finishTitle: q.start_fixed ? spotTitle(q.checkpoints.find((s) => s.kind === "finish")) : null,
      status: q.status,
      createdAt: q.created_at,
      publishedAt: q.published_at ?? null,
      questType: "route_trace",
    };
  }
  return null;
}

/** Facts for an auto summary; null when the quest already has a creator description. */
export function questAutoSummaryInput(q: QuestDetail): QuestSeoFacts | null {
  if (q.description?.trim()) return null;
  return questSeoFactsFromDetail(q);
}

/** One or two short sentences built only from quest data (never invented). */
export function formatQuestAutoSummary(facts: QuestSeoFacts): string {
  return questAutoSummary(facts, toSeoLocale(locale()));
}

export function questAutoSummaryText(q: QuestDetail): string | null {
  const input = questAutoSummaryInput(q);
  return input ? formatQuestAutoSummary(input) : null;
}

export function questPageMetaForDetail(q: QuestDetail): string {
  const facts = questSeoFactsFromDetail(q);
  if (!facts) return q.description?.trim() ?? "";
  return questMetaDescriptionText(facts, toSeoLocale(locale()));
}
